import type { RhythmConfig } from '../rhythm/config.ts';
import { validateRhythmConfig } from '../rhythm/config.ts';
import { generateRhythmQuestions } from '../rhythm/exercise.ts';
import { requiredLessonsBefore } from '../curriculum/resolve.ts';
import { PLACEMENT_STAGES } from './placement.ts';

/**
 * Rhythm placement: five short stages, one per rhythm unit 1–5, three rhythms
 * each, one take per rhythm, no Listen and no feedback. The first stage below
 * its pass mark sets the start (the first lesson of that unit); passing every
 * stage starts at unit 6. Earlier lessons are recorded as placed, exactly like
 * note-reading placement (same session kind, same finalize rule).
 */
export interface RhythmPlacementStage {
  id: 'pulse' | 'groupings' | 'subdivision' | 'triplets' | 'compound';
  title: string;
  /** One question per part, so every stage covers several meters. */
  parts: RhythmConfig[];
  passCorrect: number;
  /** Lesson to start at if this stage is not passed. */
  failStartsAt: string;
}

function part(p: Partial<RhythmConfig> & Pick<RhythmConfig, 'timeSignature'>): RhythmConfig {
  return { tempo: 66, measures: 2, noteValues: ['half', 'quarter'], dotted: false, rests: false, ties: false, triplets: false, questionCount: 1, ...p };
}

const QE = ['quarter', 'eighth'] as RhythmConfig['noteValues'];
const HQE = ['half', 'quarter', 'eighth'] as RhythmConfig['noteValues'];
const QES = ['quarter', 'eighth', 'sixteenth'] as RhythmConfig['noteValues'];

export const RHYTHM_PLACEMENT_STAGES: readonly RhythmPlacementStage[] = [
  {
    id: 'pulse',
    title: 'Steady pulse',
    parts: [
      part({ timeSignature: '4/4', noteValues: ['whole', 'half', 'quarter'] }),
      part({ timeSignature: '2/4', tempo: 70 }),
      part({ timeSignature: '2/2', tempo: 60, noteValues: ['whole', 'half', 'quarter'] }),
    ],
    passCorrect: 2,
    failStartsAt: 'rh-u1-steady-beat',
  },
  {
    id: 'groupings',
    title: 'Beat groupings',
    parts: [
      part({ timeSignature: '3/4', dotted: true }),
      part({ timeSignature: '5/4', grouping: [3, 2], dotted: true, measures: 1 }),
      part({ timeSignature: '7/4', grouping: [2, 3, 2], dotted: true, measures: 1 }),
    ],
    passCorrect: 2,
    failStartsAt: 'rh-u2-waltz',
  },
  {
    id: 'subdivision',
    title: 'Dividing the beat',
    parts: [
      part({ timeSignature: '4/4', tempo: 72, noteValues: HQE, rests: true }),
      part({ timeSignature: '2/4', tempo: 60, noteValues: QES, dotted: true }),
      part({ timeSignature: '3/4', tempo: 76, noteValues: HQE, ties: true }),
    ],
    passCorrect: 2,
    failStartsAt: 'rh-u3-eighths',
  },
  {
    id: 'triplets',
    title: 'Triplets',
    parts: [
      part({ timeSignature: '2/4', tempo: 60, noteValues: QE, triplets: true }),
      part({ timeSignature: '4/4', tempo: 66, noteValues: QE, triplets: true }),
      part({ timeSignature: '2/2', tempo: 60, noteValues: ['half', 'quarter'], triplets: true }),
    ],
    passCorrect: 2,
    failStartsAt: 'rh-u4-eighth-triplets',
  },
  {
    id: 'compound',
    title: 'Compound meters',
    parts: [
      part({ timeSignature: '6/8', tempo: 60, noteValues: QE }),
      part({ timeSignature: '9/8', tempo: 56, noteValues: QE, measures: 1 }),
      part({ timeSignature: '12/8', tempo: 50, noteValues: QE, measures: 1 }),
    ],
    passCorrect: 2,
    failStartsAt: 'rh-u5-six-eight',
  },
];

export const RHYTHM_PLACEMENT_ALL_PASSED_STARTS_AT = 'rh-u6-simple';

/** Display titles for every placement stage id (both paths). */
export const PLACEMENT_STAGE_TITLES: Readonly<Record<string, string>> = Object.fromEntries(
  [...PLACEMENT_STAGES, ...RHYTHM_PLACEMENT_STAGES].map((s) => [s.id, s.title]),
);

export function rhythmPlacementStageConfigs(stage: RhythmPlacementStage): RhythmConfig[] {
  return stage.parts.map((p, i) => {
    const result = validateRhythmConfig(p, { minQuestions: 1 });
    if (!result.ok) throw new Error(`Rhythm placement stage ${stage.id} part ${i} invalid: ${result.issues.map((x) => x.message).join(' ')}`);
    return result.value;
  });
}

/** All rhythm placement questions, stage by stage, positions numbered continuously. */
export function generateRhythmPlacementQuestions(seed: string) {
  const out: (ReturnType<typeof generateRhythmQuestions>[number] & { stageId: RhythmPlacementStage['id'] })[] = [];
  for (const stage of RHYTHM_PLACEMENT_STAGES) {
    rhythmPlacementStageConfigs(stage).forEach((config, i) => {
      for (const q of generateRhythmQuestions(config, `${seed}:${stage.id}:${i}`)) out.push({ ...q, position: out.length, stageId: stage.id });
    });
  }
  return out;
}

/** The stage → start mapping stored on the session (finalize reads it; see note-reading placement). */
export function rhythmPlacementMapping() {
  return {
    stages: RHYTHM_PLACEMENT_STAGES.map((stage) => ({
      id: stage.id,
      passCorrect: stage.passCorrect,
      failStartsAt: stage.failStartsAt,
      placedLessonIds: requiredLessonsBefore(stage.failStartsAt),
    })),
    allPassedStartsAt: RHYTHM_PLACEMENT_ALL_PASSED_STARTS_AT,
    allPassedPlacedLessonIds: requiredLessonsBefore(RHYTHM_PLACEMENT_ALL_PASSED_STARTS_AT),
  };
}
